import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { DeleteIconComponent } from '@itero/ui-toolkit-angular/icons';
import { TranslateModule } from '@ngx-translate/core';
import { IntuitiveIndication, IntuitiveToothForTreatment } from '../../models';
import { RxIntuitiveIndicationBadgeComponent } from '../rx-intuitive-indication-badge/rx-intuitive-indication-badge.component';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { TOOTH_EDITOR_TOOTH_NUMBER_SEPARATOR } from '@modules/rx-intuitive-teeth-diagram/models/consts';
import { RxIntuitiveIndicationRulesService } from '@modules/rx-intuitive-teeth-diagram/services/rx-intuitive-indication-rules.service';
import { RxIntuitiveToothEditorBodyComponent } from '../rx-intuitive-tooth-editor-body/rx-intuitive-tooth-editor-body.component';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';

export type ToothEditorResetEvent = {
	teeth: IntuitiveToothForTreatment[];
};

@Component({
	selector: 'rx-intuitive-tooth-editor',
	templateUrl: './rx-intuitive-tooth-editor.component.html',
	styleUrls: ['./rx-intuitive-tooth-editor.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		TlkButtonComponent,
		RxIntuitiveIndicationBadgeComponent,
		DeleteIconComponent,
		RxIntuitiveToothEditorBodyComponent
	]
})
export class RxIntuitiveToothEditorComponent {
	@Input() teeth: IntuitiveToothForTreatment[] = [];
	@Input() canEdit = true;
	@Input() canDelete = true;

	@Output() delete = new EventEmitter<ToothEditorResetEvent>();
	@Output() crownFormChanged = new EventEmitter<CrownForm>();
	@Output() additionalInfoApplied = new EventEmitter<{ toothIds: number[]; result: AdditionalInfoResult }>();
	@Output() clearDisabledFields = new EventEmitter<{ toothIds: number[]; payload: Partial<Record<string, unknown>> }>();

	teethFlat$ = this.teethDiagramQuery.teethFlat$;

	constructor(
		private readonly indicationsService: RxIntuitiveIndicationRulesService,
		private readonly teethDiagramQuery: TeethDiagramQuery
	) {}

	get indicationId(): IntuitiveIndication {
		return this.teeth[0]?.indicationId ?? this.indicationsService.getDefaultIndication();
	}

	get isIndicationVisible(): boolean {
		return this.indicationId !== this.indicationsService.getDefaultIndication();
	}

	get toothNumbersLabel(): string {
		return this.teeth.map(t => t.toothNumberInSystem).join(TOOTH_EDITOR_TOOTH_NUMBER_SEPARATOR);
	}

	get shouldHideProperties(): boolean {
		return (
			!this.teeth[0] ||
			!this.indicationsService.shouldShowToothEditorProperties({
				indicationId: this.teeth[0].indicationId
			})
		);
	}

	signalDelete(): void {
		this.delete.emit({ teeth: this.teeth });
	}

	onBodyCrownFormChanged(crownForm: CrownForm): void {
		this.crownFormChanged.emit(crownForm);
	}

	onBodyAdditionalInfoApplied(event: { toothIds: number[]; result: AdditionalInfoResult }): void {
		this.additionalInfoApplied.emit(event);
	}

	onBodyClearDisabledFields(event: { toothIds: number[]; payload: Partial<Record<string, unknown>> }): void {
		this.clearDisabledFields.emit(event);
	}
}
